import type { LiveSummary, Platform } from '@multimod/shared';

export interface MeConnection {
  externalId: string;
  displayName: string;
  scopes: string[];
  missingScopes: string[];
}

export interface MeDTO {
  user: { id: string };
  connections: Partial<Record<Platform, MeConnection>>;
  devMockActions: boolean;
}

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly code?: string) {
    super(`api_error_${status}`);
  }
}

export async function getMe(): Promise<MeDTO | null> {
  const res = await fetch('/me', { credentials: 'include' });
  if (res.status === 401) return null;
  if (!res.ok) throw new ApiError(res.status);
  return (await res.json()) as MeDTO;
}

export async function logout(): Promise<void> {
  const res = await fetch('/auth/logout', { method: 'POST', credentials: 'include' });
  if (!res.ok && res.status !== 204) throw new ApiError(res.status);
}

export async function revokeSelf(): Promise<void> {
  const res = await fetch('/me/revoke', { method: 'POST', credentials: 'include' });
  if (!res.ok && res.status !== 204) throw new ApiError(res.status);
}

export async function fetchActiveLives(): Promise<LiveSummary[]> {
  const res = await fetch('/me/streams', { credentials: 'include' });
  if (res.status === 401) throw new ApiError(401, 'unauthenticated');
  if (!res.ok) throw new ApiError(res.status);
  const body = (await res.json()) as { lives: LiveSummary[] };
  return body.lives;
}

// #144: re-resolve de uma live YouTube por videoId. /me/streams só cobre Twitch
// (a API do YouTube não lista lives de canais moderados), então o auto-refresh de
// viewerCount de live YouTube anexada repolla este endpoint. 404 = live encerrada.
export async function resolveYoutubeLive(videoId: string): Promise<LiveSummary> {
  const res = await fetch(
    `/live-discovery/youtube/resolve?videoId=${encodeURIComponent(videoId)}`,
    { credentials: 'include' },
  );
  if (res.status === 401) throw new ApiError(401, 'unauthenticated');
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(res.status, body.error);
  }
  const body = (await res.json()) as { live: LiveSummary };
  return body.live;
}

// Detach explícito de uma plataforma anexada do DO da sala primária.
// Contrato do worker (#129): DELETE /chat/attach?platform=X&primaryPlatform=Y&liveChatId=Z.
// 200 = sucesso (detached). 404 = não estava anexada (idempotente do lado do mod).
// 4xx outros = erro real (UI deve reverter).
export interface DetachLiveInput {
  detachPlatform: Platform;
  primaryPlatform: Platform;
  primaryLiveChatId: string;
}

export async function detachLive({
  detachPlatform,
  primaryPlatform,
  primaryLiveChatId,
}: DetachLiveInput): Promise<void> {
  const qs = new URLSearchParams({
    platform: detachPlatform,
    primaryPlatform,
    liveChatId: primaryLiveChatId,
  });
  const res = await fetch(`/chat/attach?${qs}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  // 404 not_attached = estado convergido (race com outro tab/reconnect).
  // Trata como sucesso pra não reverter UI otimista que já fez sentido.
  if (res.status === 404) return;
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { errorCode?: string };
    throw new ApiError(res.status, body.errorCode);
  }
}
